import { useQuery } from "@tanstack/react-query";
import { crDateOf } from "@despejo/domain";
import type { FilingPackageInput, PackageReceipt, PackageTransaction, UserExplanation } from "@despejo/tax-engine";
import { ledgerToIvaInput, type LedgerTx } from "./ivaMapping.js";
import { must, supabase } from "./supabase.js";
import type { ObligationRow } from "./types.js";

export type MemberRole = "owner" | "preparer" | "reviewer" | "viewer";

export const ROLE_LABEL: Record<MemberRole, string> = {
  owner: "Propietario",
  preparer: "Preparador",
  reviewer: "Revisor (contador)",
  viewer: "Solo lectura",
};

export const can = {
  write: (r: MemberRole | null | undefined) => r === "owner" || r === "preparer",
  review: (r: MemberRole | null | undefined) => r === "owner" || r === "reviewer",
};

/** The signed-in user's id and role in an organization. */
export function useMyRole(org: string) {
  return useQuery({
    queryKey: ["my-role", org],
    queryFn: async () => {
      const { data } = await supabase.auth.getSession();
      const uid = data.session?.user.id ?? null;
      if (!uid) return { userId: null, role: null };
      const rows = must(await supabase.from("organization_members").select("role").eq("organization_id", org).eq("user_id", uid)) as { role: MemberRole }[];
      return { userId: uid, role: rows[0]?.role ?? null };
    },
  });
}

export interface SavedPackage {
  id: string;
  package_sha256: string;
  blocking_count: number;
  status: "PREPARED" | "APPROVED";
  approved_by: string | null;
  approved_at: string | null;
  approval_note: string | null;
  saved_at: string;
  input_sha256: string | null;
  explanations: UserExplanation[];
}

export interface FilingData {
  /** Package input without the user's explanations (the component adds them). */
  base: Omit<FilingPackageInput, "userExplanations">;
  draftCapturedAt: string | null;
  latest: SavedPackage | null;
}

type TxRow = Omit<LedgerTx, "lines" | "receipt_acceptance" | "receipt_doc_type"> & { receipt_id: string | null; review_status: string; currency: string };
type ReceiptRow = {
  id: string;
  clave: string;
  direction: "ISSUED" | "RECEIVED";
  doc_type: string;
  issue_date: string;
  currency: string;
  total: string;
  total_tax: string;
  hacienda_status: string;
  acceptance_status: string;
  acceptance_due_date: string | null;
};

export const filingKeys = {
  data: (ob: string) => ["filing-data", ob] as const,
  history: (ob: string) => ["filing-history", ob] as const,
  comments: (ob: string) => ["filing-comments", ob] as const,
};

/** Everything the filing package needs for one IVA_GENERAL obligation (amounts as ::text, ADR-0003). */
export function useFilingData(row: ObligationRow) {
  return useQuery({
    queryKey: filingKeys.data(row.id),
    queryFn: async (): Promise<FilingData> => {
      const txs = must(
        await supabase
          .from("transactions")
          .select(
            "id, direction, description, has_electronic_receipt, business_use, receipt_id, review_status, currency, subtotal:subtotal::text, tax_amount:tax_amount::text, total_crc:total_crc::text, exchange_rate:exchange_rate::text, business_pct:business_pct::text",
          )
          .eq("taxpayer_id", row.taxpayer_id)
          .eq("tax_period", row.period_key)
          .is("excluded_reason", null),
      ) as TxRow[];
      const ids = txs.map((t) => t.id);
      const lines = ids.length
        ? (must(
            await supabase
              .from("transaction_lines")
              .select("transaction_id, line_no, exemption, tax_category, taxable_base:taxable_base::text, iva_rate:iva_rate::text, iva_amount:iva_amount::text")
              .in("transaction_id", ids),
          ) as (LedgerTx["lines"][number] & { transaction_id: string })[])
        : [];
      const recIds = txs.map((t) => t.receipt_id).filter((x): x is string => Boolean(x));
      const recs = recIds.length
        ? (must(
            await supabase
              .from("electronic_receipts")
              .select("id, clave, direction, doc_type, issue_date, currency, total:total::text, total_tax:total_tax::text, hacienda_status, acceptance_status, acceptance_due_date")
              .in("id", recIds),
          ) as ReceiptRow[])
        : [];
      const byRec = new Map(recs.map((r) => [r.id, r]));

      const calc = must(
        await supabase.from("calculations").select("inputs").eq("obligation_id", row.id).order("created_at", { ascending: false }).limit(1),
      ) as { inputs: { cardRetentions?: string; priorCreditBalance?: string; provisionalProportionPct?: string | null } }[];
      const prev = calc[0]?.inputs ?? {};
      const snap = must(
        await supabase.from("prefill_snapshots").select("fields, captured_at").eq("obligation_id", row.id).eq("form", "150").order("captured_at", { ascending: false }).limit(1),
      ) as { fields: Record<string, string | null>; captured_at: string }[];
      const pkgs = must(
        await supabase
          .from("filing_packages")
          .select("id, package_sha256, blocking_count, status, approved_by, approved_at, approval_note, saved_at, input_sha256:package->>inputSha256, explanations:package->explanations")
          .eq("obligation_id", row.id)
          .order("saved_at", { ascending: false })
          .limit(1),
      ) as (Omit<SavedPackage, "explanations"> & { explanations: UserExplanation[] | null })[];

      const ledger = txs.map<LedgerTx>((t) => ({
        ...t,
        lines: lines.filter((l) => l.transaction_id === t.id),
        receipt_acceptance: t.receipt_id ? (byRec.get(t.receipt_id)?.acceptance_status ?? null) : null,
        receipt_acceptance_due: t.receipt_id ? (byRec.get(t.receipt_id)?.acceptance_due_date ?? null) : null,
        receipt_doc_type: t.receipt_id ? (byRec.get(t.receipt_id)?.doc_type ?? null) : null,
      }));
      const { input: iva, issues } = ledgerToIvaInput(row.period_key, ledger, {
        provisionalProportionPct: prev.provisionalProportionPct ?? null,
        cardRetentions: prev.cardRetentions ?? "0",
        priorCreditBalance: prev.priorCreditBalance ?? "0",
      });
      const transactions: PackageTransaction[] = txs.map((t) => ({
        id: t.id,
        label: t.description ?? t.id,
        direction: t.direction,
        reviewStatus: t.review_status,
        currency: t.currency,
        exchangeRate: t.exchange_rate,
        receiptId: t.receipt_id,
      }));
      const receipts: PackageReceipt[] = txs.flatMap((t) => {
        const r = t.receipt_id ? byRec.get(t.receipt_id) : undefined;
        if (!r) return [];
        return [
          {
            id: r.id,
            clave: r.clave,
            direction: r.direction,
            docType: r.doc_type,
            issueDate: r.issue_date,
            currency: r.currency,
            total: r.total,
            totalTax: r.total_tax,
            haciendaStatus: r.hacienda_status,
            acceptanceStatus: r.acceptance_status,
            acceptanceDueDate: r.acceptance_due_date,
            transactionId: t.id,
          },
        ];
      });
      const fields = snap[0]?.fields ?? null;
      const draft = fields
        ? (Object.fromEntries(Object.entries(fields).filter((e): e is [string, string] => typeof e[1] === "string" && e[1] !== "")) as Record<string, string>)
        : null;
      const latest = pkgs[0] ? { ...pkgs[0], explanations: pkgs[0].explanations ?? [] } : null;
      return {
        base: {
          obligation: {
            id: row.id,
            taxpayerId: row.taxpayer_id,
            obligationCode: row.obligation_code,
            periodKey: row.period_key,
            dueDate: row.due_date,
            provisional: row.provisional,
            filingStatus: row.filing_status,
          },
          asOf: crDateOf(new Date()),
          iva,
          mappingIssues: issues,
          transactions,
          receipts,
          draft: draft && Object.keys(draft).length ? draft : null,
        },
        draftCapturedAt: snap[0]?.captured_at ?? null,
        latest,
      };
    },
  });
}

export interface FilingHistoryRow {
  id: string;
  version: number;
  amends_filing_id: string | null;
  filed_at: string;
  is_late: boolean;
  amount_declared: string | null;
  reason: string | null;
  package_id: string | null;
  package_sha256: string | null;
  figures_diff: Record<string, { before: string | null; after: string | null; diff: string | null }> | null;
  recorded_at: string;
}

export function useFilingHistory(obligationId: string) {
  return useQuery({
    queryKey: filingKeys.history(obligationId),
    queryFn: async () =>
      must(
        await supabase
          .from("filings")
          .select("id, version, amends_filing_id, filed_at, is_late, amount_declared:amount_declared::text, reason, package_id, package_sha256, figures_diff, recorded_at")
          .eq("obligation_id", obligationId)
          .order("version", { ascending: false }),
      ) as FilingHistoryRow[],
  });
}

export interface CommentRow {
  id: string;
  author: string;
  body: string;
  created_at: string;
  package_id: string | null;
}

export function usePackageComments(obligationId: string) {
  return useQuery({
    queryKey: filingKeys.comments(obligationId),
    queryFn: async () =>
      must(
        await supabase.from("filing_package_comments").select("id, author, body, created_at, package_id").eq("obligation_id", obligationId).order("created_at"),
      ) as CommentRow[],
  });
}

export function useMembers(org: string) {
  return useQuery({
    queryKey: ["members", org],
    queryFn: async () => must(await supabase.rpc("list_members", { p_org: org })) as { user_id: string; email: string | null; role: MemberRole; created_at: string }[],
  });
}
